import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'

import { ApolloProvider } from '@apollo/client'
import { useLingui } from '@lingui/react'
import {
  useErrorBoundaryContext,
  useErrorModal,
} from '@twistopayments/design-system/components/molecules/error-boundary'

import { getHeaders } from '@twisto/api-common'
import type { I18nLanguage } from '@twisto/base'
import { useAppConfig } from '@twisto/base/app-config'

import { useRiskHeaders } from '../risk-headers-provider'

import { setupFlowApi } from './config'

type Props = {
  children: ReactNode
}
export const FlowClientProvider = ({ children }: Props) => {
  const {
    i18n: { locale },
  } = useLingui()
  const { region, FEDERATED_GRAPHQL_ENDPOINT } = useAppConfig()
  const { setError } = useErrorBoundaryContext()
  const [apiClient, setApiClient] = useState<ReturnType<typeof setupFlowApi>>()

  const { showErrorWithContinue, showDefaultError } = useErrorModal()

  const riskHeaders = useRiskHeaders()

  useEffect(() => {
    const setupApiClient = async () => {
      setApiClient(
        setupFlowApi({
          config: {
            uri: FEDERATED_GRAPHQL_ENDPOINT,
            // @see https://developer.mozilla.org/en-US/docs/Web/API/Request/credentials#Value
            credentials: 'include',
          },
          getHeaders: () => ({
            ...getHeaders({
              language: locale as I18nLanguage,
              region,
            }),
            // Risk headerss
            ...riskHeaders,
          }),
          setError,
          showErrorWithContinue,
          showDefaultError,
        })
      )
    }

    setupApiClient()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, region, riskHeaders])

  if (!apiClient) {
    return null
  }

  return <ApolloProvider client={apiClient}>{children}</ApolloProvider>
}
